/**
 * Hover-track magnifier for the Fabled Figs product gallery.
 *
 * The CSS in product-fabled.css scales the hero image to 2× on hover. This
 * script updates `transform-origin` as the cursor moves over the image so
 * the zoom focal point follows the mouse — same behaviour as AmericanBricks
 * and most BigCommerce themes ship with.
 *
 * Scoped to product.fabled-hero pages via the data-template attribute, and
 * uses event delegation on <main> so dynamically-rendered carousel slides
 * are picked up without rebinding.
 */
(function () {
  const main = document.querySelector('main[data-template="product.fabled-hero"]');
  if (!main) return;

  const CONTAINER_SELECTOR = '.product-media-container';

  function getContainer(event) {
    return event.target.closest(CONTAINER_SELECTOR);Q
  }

  main.addEventListener('mousemove', (event) => {
    const container = getContainer(event);
    if (!container) return;

    const img = container.querySelector('img');
    if (!img) return;

    const rect = container.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    img.style.transformOrigin = `${x}% ${y}%`;
  });

  // Reset origin when leaving so the next hover starts from center.
  main.addEventListener('mouseout', (event) => {
    const container = getContainer(event);
    if (!container) return;
    // Skip resets that happen while moving between children of the same container.
    if (container.contains(event.relatedTarget)) return;

    const img = container.querySelector('img');
    if (img) img.style.transformOrigin = 'center center';
  });
})();

/**
 * Thumbnail scroll arrows for the Fabled Figs product gallery.
 *
 * The stock thumbnail strip (.slideshow-controls__thumbnails-container) scrolls
 * horizontally, but in this layout it shows no arrows, so shoppers may not
 * realize there are more images. We inject a prev/next arrow pair just below
 * each thumbnail strip and wire them to scroll it. The arrows hide themselves
 * when everything already fits, and disable at each end. Styled in
 * product-fabled.css; stock gallery markup is left untouched.
 */
(function () {
  const main = document.querySelector('main[data-template="product.fabled-hero"]');
  if (!main) return;

  // Simple chevron glyphs (matches the main gallery once its icon style is set
  // to "chevron"), so the thumbnail arrows and the big display arrows match.
  const CHEVRON_LEFT =
    '<svg viewBox="0 0 12 20" aria-hidden="true" focusable="false"><path d="M10 1 2 10l8 9" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const CHEVRON_RIGHT =
    '<svg viewBox="0 0 12 20" aria-hidden="true" focusable="false"><path d="M2 1l8 9-8 9" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function setup(container) {
    if (container.dataset.ffThumbNav) return;
    container.dataset.ffThumbNav = '1';

    // Attach arrows to the non-scrolling controls wrapper, not the scroll
    // container itself (which would carry the arrows away as it scrolls).
    const host = container.closest('slideshow-controls') || container.parentElement || container;
    host.classList.add('ff-thumb-host');

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'ff-thumb-nav__btn ff-thumb-nav__btn--prev';
    prev.setAttribute('aria-label', 'Scroll thumbnails left');
    prev.innerHTML = CHEVRON_LEFT;

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'ff-thumb-nav__btn ff-thumb-nav__btn--next';
    next.setAttribute('aria-label', 'Scroll thumbnails right');
    next.innerHTML = CHEVRON_RIGHT;

    host.appendChild(prev);
    host.appendChild(next);

    function step() {
      return Math.max(container.clientWidth * 0.6, 120);
    }
    prev.addEventListener('click', function () {
      container.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    next.addEventListener('click', function () {
      container.scrollBy({ left: step(), behavior: 'smooth' });
    });

    function update() {
      const scrollable = container.scrollWidth - container.clientWidth > 2;
      const max = container.scrollWidth - container.clientWidth - 1;
      // Hide both when the strip can't scroll (everything fits, or this set of
      // controls is hidden at the current breakpoint). Otherwise hide whichever
      // direction has no more room, so there's never a dead arrow over an end image.
      prev.hidden = !scrollable || container.scrollLeft <= 1;
      next.hidden = !scrollable || container.scrollLeft >= max;
    }
    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    if (window.ResizeObserver) {
      new ResizeObserver(update).observe(container);
    }
    update();
  }

  function init() {
    main.querySelectorAll('.slideshow-controls__thumbnails-container').forEach(setup);
  }

  init();
  // Re-run after full load in case the gallery hydrates late.
  window.addEventListener('load', init);
})();

/**
 * "Read more" on the product description.
 *
 * Every branded (ffpd-) description opens with one lead paragraph and then
 * runs through several sections, so on phones shoppers had to scroll past all
 * of it to reach the reviews and FAQ. This folds away everything after the
 * lead behind a Read more / Show less button.
 *
 * Two things never fold: the gold heads-up box (single-stud bodies, resin
 * care) and the small grey fine print (figure not included, small parts, not
 * affiliated with LEGO). Shoppers need those before they buy.
 *
 * The full text stays in the page (search engines still read it) and is only
 * hidden once this runs, so if the script fails, the description simply shows
 * in full. A description with nothing after its lead gets no button.
 */
(function () {
  const main = document.querySelector('main[data-template="product.fabled-hero"]');
  if (!main) return;

  const KEEP_SELECTOR = '.ffpd-compat, .ffpd-care, .ffpd-fineprint';
  const MORE_LABEL = 'Read more';
  const LESS_LABEL = 'Show less';
  const CHEVRON_DOWN =
    '<svg viewBox="0 0 14 9" aria-hidden="true" focusable="false"><path d="M1.5 1.5 7 7l5.5-5.5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function hasContent(el) {
    return el.textContent.trim() !== '' || el.querySelector('img, video, iframe') !== null;
  }

  function setup(lead) {
    // The text block holding the description. Falls back to the outermost
    // .ffpd so the walk below can never reach the blocks around it.
    let root = lead.closest('.text-block');
    if (!root) {
      for (let el = lead.parentElement; el && el !== main; el = el.parentElement) {
        if (el.classList.contains('ffpd')) root = el;
      }
    }
    if (!root || root.querySelector('.ff-desc-toggle')) return;

    // Everything after the lead, at every level up to the root. Some pasted
    // descriptions nest the template inside itself, so one level isn't enough.
    const fold = [];
    for (let node = lead; node && node !== root; node = node.parentElement) {
      for (let el = node.nextElementSibling; el; el = el.nextElementSibling) {
        if (el.matches(KEEP_SELECTOR) || el.matches('style, script, template')) continue;
        fold.push(el);
      }
    }
    if (!fold.some(hasContent)) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ff-desc-toggle';
    button.setAttribute('aria-expanded', 'false');
    button.innerHTML = '<span class="ff-desc-toggle__label">' + MORE_LABEL + '</span>' + CHEVRON_DOWN;
    const label = button.firstElementChild;

    // The button sits just above the fine print, so it reads "Read more" right
    // under the lead while folded and "Show less" at the end of the text once
    // open, without ever moving in the page.
    const scope = lead.parentElement;
    scope.insertBefore(button, scope.querySelector(':scope > .ffpd-fineprint'));

    fold.forEach(function (el) {
      el.classList.add('ff-desc-fold', 'ff-desc-hidden');
    });

    button.addEventListener('click', function () {
      const open = button.getAttribute('aria-expanded') !== 'true';
      fold.forEach(function (el) {
        el.classList.toggle('ff-desc-hidden', !open);
      });
      button.setAttribute('aria-expanded', String(open));
      label.textContent = open ? LESS_LABEL : MORE_LABEL;

      // Browsers that hold the scroll position can leave the newly shown text
      // above the screen, or the button above it after folding back up. Bring
      // whichever one the shopper needs next back into view.
      const target = open ? fold.find(hasContent) : button;
      const rect = target.getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight) {
        target.scrollIntoView({ block: open ? 'start' : 'center' });
      }
    });
  }

  function init() {
    const lead = main.querySelector('.ffpd-lead');
    if (lead) setup(lead);
  }

  init();
  // Combined listings and the theme editor redraw the product area, which
  // drops the button. Set it up again on the new markup.
  document.addEventListener('variant:update', init);
  document.addEventListener('shopify:section:load', init);
})();
